export interface IMarkerMetrics {
  main: {
    width: number;
    height: number;
  };
  text: {
    fontSize: number;
    padding: number;
    y: number;
    width: number;
    height: number;
    radius: number;
    borderSize: number;
    backgroundWidth: number;
    backgroundHeight: number;
    backgroundX: number;
    backgroundY: number;
  };
  image: {
    containerWidth: number;
    containerHeight: number;
    containerRadius: number;
    containerX: number;
    containerY: number;
    width: number;
    height: number;
    x: number;
    y: number;
  };
  triangle: {
    size: number;
    x: number;
    y: number;
  };
  point: {
    padding: number;
    size: number;
    x: number;
    y: number;
    checkX: number;
    checkY: number;
    checkSize: number;
  };
}

const defaultImageContainerMargin = 14;

export const getMarkerMetrics = ({
  width = 70,
  height = 90,
  imageContainerRadius = 10,
  fontSize = 14,
  textPadding = 6,
  textContainerRadius = 14,
  textBorderSize = 1.5,
  triangleSize = 8,
  pointPadding = 4,
  imageWidth = 34,
  imageHeight = 34,
  imageContainerMargin = defaultImageContainerMargin,
  scale = 1,
  pointSizeScale = 1,
}): IMarkerMetrics => {
  // main container sizes
  const sWidth = width * scale;
  let sHeight = height * scale;

  // text
  const sFontSize = fontSize * scale;
  const sTextPadding = textPadding * scale;
  const sTextBorderSize = textBorderSize * scale;
  const sTextWidth = sWidth;
  const sTextHeight = sFontSize + (sTextPadding + sTextBorderSize) * 2;
  const sTextRadius = textContainerRadius * scale;
  const sTextBackgroundWidth = sTextWidth - sTextBorderSize * 2;
  const sTextBackgroundHeight = sTextHeight - sTextBorderSize * 2;
  const sTextBackgroundX = sTextBorderSize;
  const sTextBackgroundY = sTextBorderSize;
  const sTextY = sTextHeight / 2 + sTextBackgroundY;

  // image
  const sImageContainerWidth = sWidth * 0.8;
  const sImageContainerHeight = sHeight * 0.6;
  const sImageContainerX = (sWidth - sImageContainerWidth) / 2;
  const sImageContainerY = imageContainerMargin * scale;
  const sImageContainerRadius = imageContainerRadius * scale;
  const sImageWidth = imageWidth * scale;
  const sImageHeight = imageHeight * scale;
  const sImageY = sTextHeight;
  const sImageX = (sWidth - sImageWidth) / 2;

  // triangle
  const sTirangleSize = triangleSize * scale;
  const sTriangleX = sWidth / 2 - sTirangleSize;
  const sTriangleY = sImageContainerHeight + sImageContainerY - 1;

  // point
  const sPointPadding = pointPadding;
  const sPointSize = (sHeight - sTriangleY - sTirangleSize - sPointPadding) * pointSizeScale;

  sHeight += sPointSize * (pointSizeScale - 1);
  const sPointX = (sWidth - pointSizeScale) / 2;
  const sPointY = sHeight - sPointSize / 2;
  const sPointCheckSize = sPointSize * 0.6;
  const sPointCheckX = (sWidth - sPointCheckSize) / 2;
  const sPointCheckY = sHeight - sPointSize + (sPointSize - sPointCheckSize) / 2;

  return {
    main: {
      width: sWidth,
      height: sHeight,
    },
    text: {
      fontSize: sFontSize,
      padding: sTextPadding,
      y: sTextY,
      width: sTextWidth,
      height: sTextHeight,
      radius: sTextRadius,
      borderSize: sTextBorderSize,
      backgroundWidth: sTextBackgroundWidth,
      backgroundHeight: sTextBackgroundHeight,
      backgroundX: sTextBackgroundX,
      backgroundY: sTextBackgroundY,
    },
    image: {
      containerWidth: sImageContainerWidth,
      containerHeight: sImageContainerHeight,
      containerRadius: sImageContainerRadius,
      containerX: sImageContainerX,
      containerY: sImageContainerY,
      width: sImageWidth,
      height: sImageHeight,
      x: sImageX,
      y: sImageY,
    },
    triangle: {
      size: sTirangleSize,
      x: sTriangleX,
      y: sTriangleY,
    },
    point: {
      padding: sPointPadding,
      size: sPointSize,
      x: sPointX,
      y: sPointY,
      checkX: sPointCheckX,
      checkY: sPointCheckY,
      checkSize: sPointCheckSize,
    },
  };
};

const getDefaultMarkerBiura = (): IMarkerMetrics => {
  const defaultMarker = getMarkerMetrics({ scale: 0.9 });

  defaultMarker.main.height -= defaultImageContainerMargin;
  defaultMarker.triangle.y -= defaultImageContainerMargin;
  defaultMarker.point.y -= defaultImageContainerMargin;
  defaultMarker.image.containerY = 0;
  defaultMarker.image.y = Math.abs(defaultMarker.image.containerHeight - defaultMarker.image.height) / 2;

  return defaultMarker;
};

type TMarkerMetricKeys = 'defaultMarker' | 'defaultActiveMarker' | 'defaultMarkerBiura' | 'defaultMarkerBiura';
const markerMetrics: Record<TMarkerMetricKeys, IMarkerMetrics> = {
  defaultMarker: getMarkerMetrics({ scale: 0.9 }),
  defaultActiveMarker: getMarkerMetrics({ scale: 1.1, pointSizeScale: 1.6 }),
  defaultMarkerBiura: getDefaultMarkerBiura(),
};

export default markerMetrics;
